import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send,
  AttachFile,
  Delete,
  DeleteSweep,
  ContentPaste,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
} from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import MessageCard from './MessageCard';
import ForwardDialog from './ForwardDialog';
import { trackButtonClick, trackMessageAction } from '../utils/analytics';
import { secureStorage } from '../services/SecureStorageService';
import { Message as MessageType } from '../services/api';
import API_CONFIG from '../config/api.config';

interface MessagesListProps {
  onCopyToClipboard: (text: string) => void;
}

const MessagesList: React.FC<MessagesListProps> = observer(({ onCopyToClipboard }) => {
  const [forwardDialogOpen, setForwardDialogOpen] = React.useState(false);
  const [messageToForward, setMessageToForward] = React.useState<MessageType | null>(null);
  const [clipboardPermission, setClipboardPermission] = React.useState<PermissionState | null>(null);
  const [hasAutoFilled, setHasAutoFilled] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to compress image if needed
  const compressImageIfNeeded = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          // Calculate new dimensions (max 1920px on longest side)
          let width = img.width;
          let height = img.height;
          const maxDimension = 1920;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with JPEG compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], 'compressed-image.jpg', { type: 'image/jpeg' });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.80
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  // Check clipboard permission on mount
  React.useEffect(() => {
    const checkPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName });
          setClipboardPermission(result.state);
          
          result.addEventListener('change', () => {
            setClipboardPermission(result.state);
          });
        }
      } catch (err) {
        console.log('Clipboard permission check not supported:', err);
      }
    };
    
    checkPermission();
  }, []);

  const handlePasteFromClipboard = async () => {
    trackButtonClick('Paste from Clipboard', 'Messages List');
    try {
      if (!navigator.clipboard) {
        appStore.setError('Clipboard API not supported in this browser');
        return;
      }

      // Try to read clipboard items (for images)
      const clipboardItems = await navigator.clipboard.read();
      
      for (const item of clipboardItems) {
        // Check for image types
        const imageTypes = item.types.filter(type => type.startsWith('image/'));
        
        if (imageTypes.length > 0) {
          // Get the first image type
          const blob = await item.getType(imageTypes[0]);
          
          // Convert blob to File object
          let file = new File([blob], 'clipboard-image.png', { type: blob.type });
          
          // Compress to reduce size (clipboard gives uncompressed images)
          file = await compressImageIfNeeded(file);
          
          // Check final size
          const estimatedBase64Size = file.size * 1.37;
          
          if (estimatedBase64Size > API_CONFIG.MAX_IMAGE_SIZE) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            const estimatedMB = (estimatedBase64Size / (1024 * 1024)).toFixed(1);
            appStore.setError(`Image is ${fileSizeMB}MB (${estimatedMB}MB when encoded). This exceeds the 100MB limit even after compression.`);
            return;
          }
          
          appStore.setSelectedImage(file);
          appStore.clearError();
          return;
        }
      }
      
      // If no image, try to read text
      const text = await navigator.clipboard.readText();
      if (text) {
        appStore.setMessageText(text);
        appStore.clearError();
      } else {
        appStore.setError('No text or image found in clipboard');
      }
    } catch (err: any) {
      console.error('Failed to read clipboard:', err);
      if (err.name === 'NotAllowedError') {
        appStore.setError('Clipboard access denied. Please grant permission and try again.');
      } else {
        appStore.setError('Failed to read clipboard content');
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    trackButtonClick('Select Files', 'Messages List');
    trackMessageAction('select_files', `${files.length} files`);

    // Convert FileList to Array
    const fileArray = Array.from(files);

    // Check for invalid files
    const invalidFiles = fileArray.filter(file => {
      const estimatedBase64Size = file.size * 1.37;
      return estimatedBase64Size > API_CONFIG.MAX_IMAGE_SIZE;
    });

    if (invalidFiles.length > 0) {
      const invalidSizeMB = (invalidFiles[0].size / (1024 * 1024)).toFixed(1);
      appStore.setError(`Some files exceed the limit of ${API_CONFIG.MAX_IMAGE_SIZE / (1024 * 1024)}MB (e.g., ${invalidSizeMB}MB)`);
      return;
    }

    // Add files to store (this will upload them immediately)
    try {
      await appStore.addFiles(fileArray);
      appStore.clearError();
    } catch (err: any) {
      appStore.setError(err.message || 'Failed to upload files');
    }

    // Reset input to allow selecting the same files again
    event.target.value = '';
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Legacy single image handler for backward compatibility
    const file = event.target.files?.[0];
    if (!file) return;

    trackButtonClick('Select Image', 'Messages List');
    trackMessageAction('select_image', 'single');

    if (!file.type.startsWith('image/')) {
      appStore.setError('Please select an image file');
      return;
    }

    if (file.size > API_CONFIG.MAX_IMAGE_SIZE) {
      appStore.setError(`Image file size exceeds limit of ${API_CONFIG.MAX_IMAGE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    appStore.setSelectedImage(file);
    appStore.clearError();
    event.target.value = '';
  };

  const removeSelectedImage = () => {
    appStore.clearFileInput();
  };

  const handleDeleteMessage = (messageId: string) => {
    appStore.deleteMessage(messageId);
  };

  const handleSendMessage = () => {
    trackButtonClick('Send Message', 'Messages List');
    trackMessageAction('send', 'quick_send');
    appStore.sendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    // Check if there's an image in the clipboard
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste for images
        
        const blob = item.getAsFile();
        if (blob) {
          // Convert to File object
          let file = new File([blob], 'clipboard-image.png', { type: blob.type });
          
          // Compress to reduce size (clipboard gives uncompressed images)
          file = await compressImageIfNeeded(file);
          
          // Check final size
          const estimatedBase64Size = file.size * 1.37;
          
          if (estimatedBase64Size > API_CONFIG.MAX_IMAGE_SIZE) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            const estimatedMB = (estimatedBase64Size / (1024 * 1024)).toFixed(1);
            appStore.setError(`Image is ${fileSizeMB}MB (${estimatedMB}MB when encoded). This exceeds the 100MB limit even after compression.`);
            return;
          }
          
          appStore.setSelectedImage(file);
          appStore.clearError();
        }
        return;
      }
    }
    
    // For text, let the default paste behavior work
  };

  const handleClearAll = async () => {
    await appStore.clearAllMessages();
  };

  const handleOpenForwardDialog = (message: MessageType) => {
    trackButtonClick('Forward Message', 'Messages List');
    trackMessageAction('forward_initiate', message.type);
    setMessageToForward(message);
    setForwardDialogOpen(true);
  };

  const handleCloseForwardDialog = () => {
    setForwardDialogOpen(false);
    setMessageToForward(null);
  };

  const handleForwardToDevice = async (device: any) => {
    if (messageToForward) {
      try {
        await appStore.forwardMessage(messageToForward, device);
        // Close dialog after successful forward
        handleCloseForwardDialog();
      } catch (error) {
        console.error('Failed to forward message:', error);
        // Error is already handled in AppStore, but keep dialog open so user can try again
      }
    } else {
      console.error('No message to forward');
      appStore.setError('No message selected for forwarding');
    }
  };

  const handleTextFieldFocus = async () => {
    // Only auto-fill once per session and if fields are empty
    if (hasAutoFilled || appStore.messageText.trim() || appStore.selectedImage) {
      return;
    }

    try {
      if (!navigator.clipboard) {
        return;
      }

      // Try to read clipboard items (for images)
      const clipboardItems = await navigator.clipboard.read();
      
      for (const item of clipboardItems) {
        // Check for image types first
        const imageTypes = item.types.filter(type => type.startsWith('image/'));
        
        if (imageTypes.length > 0) {
          // Get the first image type
          const blob = await item.getType(imageTypes[0]);
          
          // Convert blob to File object
          let file = new File([blob], 'clipboard-image.png', { type: blob.type });
          
          // Compress to reduce size (clipboard gives uncompressed images)
          file = await compressImageIfNeeded(file);
          
          // Check final size
          const estimatedBase64Size = file.size * 1.37;
          
          if (estimatedBase64Size > API_CONFIG.MAX_IMAGE_SIZE) {
            // Don't auto-fill if too large even after compression
            return;
          }
          
          appStore.setSelectedImage(file);
          appStore.clearError();
          setHasAutoFilled(true);
          return;
        }
      }
      
      // If no image, try to read text
      const text = await navigator.clipboard.readText();
      if (text && text.trim()) {
        appStore.setMessageText(text);
        appStore.clearError();
        setHasAutoFilled(true);
      }
    } catch (err: any) {
      // Silently fail for auto-fill - user can still use manual paste button
      console.log('Auto-fill from clipboard not available:', err.message);
    }
  };

  // Reset auto-fill flag when selected device changes
  React.useEffect(() => {
    setHasAutoFilled(false);
  }, [appStore.selectedDevice?.id]);

  return (
    <Card sx={{ 
      height: { 
        xs: appStore.selectedDevice ? 'calc(100vh - 170px)' : 'auto', 
        md: 'auto' 
      },
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      backdropFilter: 'blur(10px)',
    }}>
      <CardContent sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&:last-child': { pb: 1 }
      }}>
        {/* Hide title on mobile when device is selected (shown in back button bar) */}
        {(!isMobile || !appStore.selectedDevice) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
              {appStore.selectedDevice ? (
                appStore.selectedDevice.deviceType === 'web' 
                  ? `Incoming Messages` 
                  : `Messages to ${appStore.selectedDevice.name}`
              ) : 'Select a device to view messages'}
            </Typography>
            
            {/* Clear All button - only show for current device with messages */}
            {appStore.selectedDevice && 
             appStore.currentDevice && 
             appStore.selectedDevice.id === appStore.currentDevice.id && 
             appStore.messages.length > 0 && (
              <IconButton 
                onClick={handleClearAll}
                color="error"
                title="Clear all messages"
                size="small"
              >
                <DeleteSweep />
              </IconButton>
            )}
          </Box>
        )}
        
        {/* Mobile: Show Clear All button separately when device is selected */}
        {isMobile && appStore.selectedDevice && 
         appStore.currentDevice && 
         appStore.selectedDevice.id === appStore.currentDevice.id && 
         appStore.messages.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <IconButton 
              onClick={handleClearAll}
              color="error"
              title="Clear all messages"
              size="small"
            >
              <DeleteSweep />
            </IconButton>
          </Box>
        )}

        {appStore.selectedDevice ? (
          <>
            {/* Messages List */}
            <Box sx={{ 
              flex: 1,
              overflow: 'auto', 
              mb: 1,
              minHeight: 0
            }}>
              {appStore.isLoadingMessages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : appStore.messages.length === 0 ? (
                // Only show "No messages yet" for current device (web browser)
                appStore.currentDevice && appStore.selectedDevice.id === appStore.currentDevice.id ? (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', py: 2 }}>
                    No messages yet
                  </Typography>
                ) : (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', py: 2 }}>
                    Type a message below to send to {appStore.selectedDevice.name}
                  </Typography>
                )
              ) : (
                appStore.messages.map((message, index) => (
                  <MessageCard
                    key={`${message.contentId}-${index}`}
                    message={message}
                    onCopy={() => message.text && onCopyToClipboard(message.text)}
                    onDelete={() => handleDeleteMessage(message.contentId)}
                    onForward={() => handleOpenForwardDialog(message)}
                    showDeleteButton={appStore.selectedDevice?.deviceType === 'web'}
                    showForwardButton={appStore.selectedDevice?.deviceType === 'web'}
                  />
                ))
              )}
            </Box>

            {/* Send Message - Only show for other devices, not current device */}
            {appStore.shouldShowMessageInput && (
              <Box sx={{ flexShrink: 0 }}>
                {/* Single Image Preview (backward compatibility) */}
                {appStore.selectedImage && appStore.imagePreview && (
                  <Box sx={{ mb: 1, p: 1, border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, backgroundColor: 'rgba(255, 255, 255, 0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                        Selected Image ({appStore.formatFileSize(appStore.selectedImage.size)})
                      </Typography>
                      <IconButton size="small" onClick={removeSelectedImage}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <img
                      src={appStore.imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 4
                      }}
                    />
                  </Box>
                )}

                {/* Multiple Files Preview */}
                {appStore.selectedFiles.length > 0 && (
                  <Box sx={{ mb: 1, p: 1, border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, backgroundColor: 'rgba(255, 255, 255, 0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                          {appStore.selectedFiles.length} file(s) selected
                          {appStore.isUploadingFiles && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#2196F3' }}>
                              (Uploading...)
                            </Typography>
                          )}
                        </Typography>
                        {appStore.sessionTotalSize > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'rgba(255, 255, 255, 0.8)' }}>
                            Total uploaded size: {appStore.formatFileSize(appStore.sessionTotalSize)}
                          </Typography>
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={removeSelectedImage}
                        disabled={appStore.isUploadingFiles}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {appStore.selectedFiles.map((file, index) => {
                        const uploadStatus = appStore.fileUploadStatus.get(index);
                        const isUploading = uploadStatus === 'uploading';
                        const isUploaded = uploadStatus === 'uploaded';
                        const hasError = uploadStatus === 'error';
                        
                        return (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              border: hasError ? '2px solid #f44336' : '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 1,
                              p: 0.5,
                              minWidth: 100,
                              maxWidth: 150,
                              opacity: isUploading ? 0.7 : 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }}
                          >
                            {file.type.startsWith('image/') && appStore.filePreviews.has(index) ? (
                              <>
                                <img
                                  src={appStore.filePreviews.get(index)}
                                  alt={file.name}
                                  style={{
                                    width: '100%',
                                    maxHeight: 100,
                                    objectFit: 'contain',
                                    borderRadius: 4
                                  }}
                                />
                              <Box sx={{ 
                                position: 'absolute', 
                                top: 2, 
                                right: 2,
                                display: 'flex',
                                gap: 0.25,
                                alignItems: 'center'
                              }}>
                                  {isUploading && (
                                    <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                                  )}
                                  {isUploaded && (
                                    <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                  )}
                                  {hasError && (
                                    <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => appStore.removeFile(index)}
                                    disabled={isUploading}
                                    sx={{
                                      bgcolor: 'rgba(255,255,255,0.8)',
                                      '&:disabled': { opacity: 0.5 }
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              </>
                            ) : (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  {isUploading && <CircularProgress size={12} />}
                                  {isUploaded && <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />}
                                  {hasError && <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                                  <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-word', flex: 1 }}>
                                    {file.name}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {appStore.formatFileSize(file.size)}
                                </Typography>
                                {hasError && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                    Upload failed
                                  </Typography>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => appStore.removeFile(index)}
                                  disabled={isUploading}
                                  sx={{ mt: 0.5 }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    size="small"
                    placeholder={(appStore.selectedImage || appStore.selectedFiles.length > 0) ? "Files selected - text will be ignored" : "Type your message or paste from clipboard..."}
                    value={appStore.messageText}
                    onChange={(e) => appStore.setMessageText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onPaste={handlePaste}
                    onFocus={handleTextFieldFocus}
                    disabled={appStore.isSending || !!appStore.selectedImage || appStore.selectedFiles.length > 0}
                    sx={{ flexBasis: { xs: '100%', sm: 'auto' } }}
                  />
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="file-upload"
                    accept="*/*"
                  />
                  
                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <IconButton
                      onClick={handlePasteFromClipboard}
                      color="info"
                      disabled={appStore.isSending}
                      title="Paste from clipboard (Ctrl+V or Cmd+V)"
                      size="small"
                    >
                      <ContentPaste />
                    </IconButton>

                    <IconButton
                      component="label"
                      htmlFor="file-upload"
                      color="secondary"
                      disabled={appStore.isSending || appStore.isUploadingFiles || (appStore.messageText.trim().length > 0 && appStore.selectedFiles.length === 0 && !appStore.selectedImage)}
                      title={appStore.messageText.trim() ? "Clear text first to attach files" : appStore.isUploadingFiles ? "Files are uploading..." : "Attach files (max 100MB each)"}
                      size="small"
                      onClick={() => trackButtonClick('Attach Files', 'Messages List')}
                    >
                      <AttachFile />
                    </IconButton>
                    
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!appStore.canSendMessage || appStore.isSending || appStore.isUploadingFiles}
                      title={appStore.isUploadingFiles ? "Please wait for files to finish uploading" : undefined}
                      size="small"
                    >
                      {appStore.isSending || appStore.isUploadingFiles ? <CircularProgress size={20} /> : <Send />}
                    </IconButton>
                  </Box>
                </Box>
                
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'rgba(255, 255, 255, 0.65)' }}>
                  {isMobile 
                    ? 'Max 100MB per file • Auto-paste on focus' 
                    : 'File size limit: 100MB per file • Multiple files supported • Clipboard content auto-fills on focus • Click paste button or press Ctrl+V/Cmd+V to paste'
                  }
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', py: 2 }}>
            Select a device from the left to start messaging
          </Typography>
        )}
      </CardContent>

      {/* Forward Dialog */}
      <ForwardDialog
        open={forwardDialogOpen}
        onClose={handleCloseForwardDialog}
        devices={appStore.devices}
        currentDeviceId={secureStorage.getDeviceId() || ''}
        onSelectDevice={handleForwardToDevice}
      />
    </Card>
  );
});

export default MessagesList;





